import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '@jaylabs/ui';
import Logo from '../components/Logo';
import './Auth.css';

function Login() {
  const bgImage = localStorage.getItem('dashboardBg') || '/backgrounds/Background-2-1024x682.jpg';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [requireTwoFactor, setRequireTwoFactor] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const isDevelopment = process.env.NODE_ENV === 'development' ||
                        window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1';
  const isBetaSubdomain = window.location.hostname === 'beta.dopedash.app' && !isDevelopment;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (isBetaSubdomain && invitationCode) {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const response = await fetch(`${API_BASE_URL}/api/invitations/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: invitationCode })
        });
        const data = await response.json();
        if (!data.valid) {
          setLoading(false);
          setError(data.message || 'Invalid invitation code');
          return;
        }
        sessionStorage.setItem('pendingInvitationCode', invitationCode);
      } catch {
        setLoading(false);
        setError('Error validating invitation code. Please try again.');
        return;
      }
    }

    const payload = { email, password };
    if (requireTwoFactor || twoFactorCode) {
      payload.twoFactorCode = twoFactorCode;
    }

    const result = await login(payload);
    setLoading(false);

    if (result.success) {
      const pendingCode = sessionStorage.getItem('pendingInvitationCode');
      if (pendingCode) {
        try {
          const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
          const token = localStorage.getItem('token');
          await fetch(`${API_BASE_URL}/api/invitations/mark-used`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ code: pendingCode })
          });
          sessionStorage.removeItem('pendingInvitationCode');
        } catch {
          // non-critical
        }
      }
      navigate('/dashboard');
    } else {
      setError(result.message);
      if (result.message?.toLowerCase().includes('two-factor')) {
        setRequireTwoFactor(true);
      }
    }
  };

  return (
    <div
      className="dd-auth-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.65), rgba(255,255,255,0.65)), url(${bgImage})`
      }}
    >
      <div className="dd-auth-card">
        <div className="dd-auth-logo">
          <Logo size="lg" clickable={false} />
        </div>
        <h1 className="dd-auth-title">Welcome back</h1>
        <p className="dd-auth-subtitle">Log in to Dope Dash</p>

        {error && <div className="dd-auth-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="dd-auth-field">
            <label htmlFor="login-email" className="dd-auth-label">Email</label>
            <Input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              size="lg"
            />
          </div>

          <div className="dd-auth-field">
            <label htmlFor="login-password" className="dd-auth-label">Password</label>
            <Input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              required
              size="lg"
            />
          </div>

          {isBetaSubdomain && (
            <div className="dd-auth-field">
              <label htmlFor="login-invite-code" className="dd-auth-label">Beta Invitation Code</label>
              <Input
                id="login-invite-code"
                type="text"
                value={invitationCode}
                onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                placeholder="BETA-XXXXX-XXXX"
                required={isBetaSubdomain}
                size="lg"
                style={{ fontFamily: 'monospace' }}
              />
              <span className="dd-auth-hint">Enter the invitation code from your beta invite email.</span>
            </div>
          )}

          {requireTwoFactor && (
            <div className="dd-auth-field">
              <label htmlFor="login-2fa" className="dd-auth-label">Two-Factor Code</label>
              <Input
                id="login-2fa"
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                placeholder="6-digit code"
                required={requireTwoFactor}
                size="lg"
                style={{ fontFamily: 'monospace', letterSpacing: '0.15em' }}
              />
              <span className="dd-auth-hint">Open your authenticator app to get the code.</span>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            style={{ width: '100%', marginTop: '0.5rem' }}
          >
            {loading ? 'Logging in…' : 'Log In'}
          </Button>
        </form>

        <div className="dd-auth-links">
          <a href="/reset">Forgot password?</a>
          <span>Don't have an account? <a href="/register">Register here</a></span>
        </div>
      </div>
    </div>
  );
}

export default Login;
